import type { FC, ReactNode } from 'react'
import type { PostSummary } from '@/types'

import Link from 'next/link'

import { cn } from '@/lib/utils'
import { getHubTheme } from '@/constants/hubs'

import { HubBadge } from '@/components/blog/hub-badge'
import { PostMeta } from '@/components/blog/post-meta'

interface CardCoverProps {
    post: PostSummary
    className?: string
}

const CardCover: FC<CardCoverProps> = ({ post, className }): ReactNode => {
    const theme = getHubTheme(post.hub)
    const Icon = theme.icon

    if (post.coverImage) {
        return (
            <img
                src={post.coverImage}
                alt={post.coverImageAlt ?? post.title}
                loading='lazy'
                className={cn('h-full w-full object-cover', className)}
            />
        )
    }

    return (
        <div className={cn('flex h-full w-full items-center justify-center', theme.softBg)}>
            <Icon className={cn('size-10', theme.text)} />
        </div>
    )
}

interface PostCardProps {
    post: PostSummary
    className?: string
}

export const PostCard: FC<PostCardProps> = ({ post, className }): ReactNode => {
    return (
        <article
            className={cn(
                'group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card transition-shadow hover:shadow-md',
                className
            )}
        >
            <div className='relative aspect-[16/10] overflow-hidden bg-muted'>
                <CardCover post={post} className='transition-transform duration-500 group-hover:scale-[1.03]' />
            </div>

            <div className='flex flex-1 flex-col p-5'>
                <HubBadge hub={post.hub} />

                <h3 className='mt-3 font-heading text-lg font-semibold leading-snug tracking-tight'>
                    <Link
                        href={`/${post.hub}/${post.slug}`}
                        className='transition-colors after:absolute after:inset-0 hover:text-primary'
                        data-mdlfy-cms={`posts:${post.id}:title`}
                    >
                        {post.title}
                    </Link>
                </h3>

                <p
                    className='mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground'
                    data-mdlfy-cms={`posts:${post.id}:excerpt`}
                >
                    {post.excerpt}
                </p>

                <PostMeta
                    postId={post.id}
                    authorName={post.authorName}
                    publishedAt={post.publishedAt}
                    readMinutes={post.readMinutes}
                    className='mt-4 border-t border-border pt-4'
                />
            </div>
        </article>
    )
}

interface FeaturedPostCardProps {
    post: PostSummary
    className?: string
}

export const FeaturedPostCard: FC<FeaturedPostCardProps> = ({ post, className }): ReactNode => {
    return (
        <article
            className={cn(
                'group relative grid overflow-hidden rounded-2xl border border-border bg-card transition-shadow hover:shadow-md md:grid-cols-2',
                className
            )}
        >
            <div className='relative aspect-[16/10] min-h-64 overflow-hidden bg-muted md:aspect-auto'>
                <CardCover post={post} className='transition-transform duration-500 group-hover:scale-[1.03]' />
            </div>

            <div className='flex flex-col justify-center p-6 sm:p-8'>
                <div className='flex items-center gap-2'>
                    <HubBadge hub={post.hub} />
                    <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                        Featured
                    </span>
                </div>

                <h2 className='mt-4 font-heading text-2xl font-bold leading-tight tracking-tight sm:text-3xl'>
                    <Link
                        href={`/${post.hub}/${post.slug}`}
                        className='transition-colors after:absolute after:inset-0 hover:text-primary'
                        data-mdlfy-cms={`posts:${post.id}:title`}
                    >
                        {post.title}
                    </Link>
                </h2>

                <p
                    className='mt-3 leading-relaxed text-muted-foreground'
                    data-mdlfy-cms={`posts:${post.id}:excerpt`}
                >
                    {post.excerpt}
                </p>

                <PostMeta
                    postId={post.id}
                    authorName={post.authorName}
                    publishedAt={post.publishedAt}
                    readMinutes={post.readMinutes}
                    className='mt-6'
                />
            </div>
        </article>
    )
}
